import { ReactNode, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface StudentLayoutProps {
  children: ReactNode;
}

export default function StudentLayout({ children }: StudentLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login-aluno');
  };

  const navItems = [
    { label: 'Meus Cursos', href: '/dashboard', icon: 'ri-book-3-line' },
    { label: 'Certificados', href: '/certificados', icon: 'ri-award-line' },
  ];

  return (
    <div className="min-h-screen bg-[#080c14]">
      {/* Subtle Background Pattern */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1020] via-[#080c14] to-[#080c14]"></div>
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/[0.02] rounded-full blur-[150px]"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-emerald-500/[0.02] rounded-full blur-[120px]"></div>
      </div>

      {/* Header - Fixed at top */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#080c14]/95 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-[72px]">
            {/* Logo */}
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <img
                src="https://static.readdy.ai/image/1cd2ecc8cebcbf00ba1926a19a1b281b/0658f006ef9b7192bc0d5e3622251d68.png"
                alt="INSOL"
                className="h-8 sm:h-9 w-auto group-hover:opacity-80 transition-opacity"
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1 bg-white/[0.02] border border-white/[0.04] rounded-full p-1">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.href;
                return (
                  <button
                    key={index}
                    onClick={() => navigate(item.href)}
                    className={`flex items-center gap-2 px-5 py-2 rounded-full text-[13px] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-gradient-to-r from-cyan-500/20 to-emerald-500/20 text-white border border-emerald-500/20'
                        : 'text-white/40 hover:text-white/70 hover:bg-white/[0.03]'
                    }`}
                  >
                    <i className={`${item.icon} ${isActive ? 'text-emerald-400' : ''}`}></i>
                    {item.label}
                  </button>
                );
              })}
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Notifications */}
              <button className="relative w-10 h-10 flex items-center justify-center bg-white/[0.03] border border-white/[0.06] rounded-xl text-white/50 hover:text-white hover:bg-white/[0.06] transition-all cursor-pointer">
                <i className="ri-notification-3-line text-lg"></i>
                <span className="absolute top-2 right-2 w-2 h-2 bg-emerald-500 rounded-full"></span>
              </button>

              {/* User Info - Desktop */}
              <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-white/[0.06]">
                <div className="text-right">
                  <p className="text-white text-[13px] font-medium leading-tight">{user?.name}</p>
                  <p className="text-white/30 text-[11px]">Aluno</p>
                </div>
              </div>

              {/* User Avatar with Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-white font-bold text-[14px] cursor-pointer hover:shadow-lg hover:shadow-emerald-500/20 transition-all duration-300 hover:scale-105"
                >
                  {user?.name.charAt(0).toUpperCase()}
                </button>

                {/* Dropdown Menu */}
                {menuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setMenuOpen(false)}
                    ></div>
                    <div className="absolute right-0 top-full mt-3 w-56 bg-[#0c1220] border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/50 z-50 overflow-hidden">
                      {/* User Info Header */}
                      <div className="px-5 py-4 bg-gradient-to-r from-cyan-500/5 to-emerald-500/5 border-b border-white/[0.06]">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-white font-bold text-[14px]">
                            {user?.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white text-[13px] font-semibold">{user?.name}</p>
                            <p className="text-white/40 text-[11px]">{user?.email}</p>
                          </div>
                        </div>
                      </div>

                      {/* Mobile Nav Items */}
                      <div className="md:hidden py-2 border-b border-white/[0.06]">
                        {navItems.map((item, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              navigate(item.href);
                              setMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-5 py-3 text-[13px] cursor-pointer transition-all whitespace-nowrap ${
                              location.pathname === item.href
                                ? 'text-emerald-400 bg-emerald-500/5'
                                : 'text-white/50 hover:text-white hover:bg-white/[0.03]'
                            }`}
                          >
                            <i className={item.icon}></i>
                            {item.label}
                          </button>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="py-2">
                        <button
                          onClick={() => {
                            navigate('/');
                            setMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-5 py-3 text-white/50 hover:text-white hover:bg-white/[0.03] text-[13px] cursor-pointer transition-all whitespace-nowrap"
                        >
                          <i className="ri-home-5-line"></i>
                          Ir para o Site
                        </button>
                        <button
                          onClick={() => setMenuOpen(false)}
                          className="w-full flex items-center gap-3 px-5 py-3 text-white/50 hover:text-white hover:bg-white/[0.03] text-[13px] cursor-pointer transition-all whitespace-nowrap"
                        >
                          <i className="ri-settings-4-line"></i>
                          Configurações
                        </button>
                        <div className="mx-4 my-2 border-t border-white/[0.06]"></div>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-5 py-3 text-red-400/70 hover:text-red-400 hover:bg-red-500/5 text-[13px] cursor-pointer transition-all whitespace-nowrap"
                        >
                          <i className="ri-logout-box-r-line"></i>
                          Sair da Conta
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Add padding-top to account for fixed header */}
      <main className="relative z-10 pt-16 sm:pt-[72px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          {children}
        </div>
      </main>
    </div>
  );
}
